/**
 * Firebase SDK configuration and initialization
 */

import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getAnalytics } from 'firebase/analytics';
import { getStorage } from 'firebase/storage';

// Validate required environment variables
const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
];

const missingVars = requiredEnvVars.filter(
  (varName) => !import.meta.env[varName] || import.meta.env[varName].includes('REPLACE')
);

if (missingVars.length > 0) {
  console.error(
    '❌ Missing or invalid Firebase configuration!\n\n' +
    'Required environment variables:\n' +
    missingVars.map(v => `  - ${v}`).join('\n') +
    '\n\nPlease:\n' +
    '1. Copy hosting/.env.example to hosting/.env\n' +
    '2. Get your Firebase config from: https://console.firebase.google.com\n' +
    '3. Go to Project Settings → General → Your apps → Web app\n' +
    '4. Copy the config values into hosting/.env\n' +
    '5. Restart the dev server\n'
  );
}

// Firebase configuration
// These values are safe to expose in client-side code
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || '',
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
export const storage = getStorage(app);

// Initialize Analytics (only in production)
export const analytics =
  import.meta.env.MODE === 'production' ? getAnalytics(app) : null;
